<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const verifications = [
  {
    id: 'VER-2024-012',
    project: 'Solar Park Beta – Northern Highlands',
    projectId: 'P-2024-0031',
    monitoringPeriod: '01 Apr 2024 – 30 Sep 2024',
    doe: 'Bureau Veritas Carbon Assurance',
    submittedDate: '2024-10-05',
    status: 'Approved',
    statusColor: 'green',
    verifiedCredits: '6,120',
    stage: 6,
    comments: [
      { date: '2024-11-20', from: 'Bureau Veritas', text: 'Verification complete. Positive verification report issued.' },
      { date: '2024-11-01', from: 'Bureau Veritas', text: 'Monitoring data validated. No material discrepancies found.' },
    ],
  },
  {
    id: 'VER-2024-019',
    project: 'Solar Park Beta – Northern Highlands',
    projectId: 'P-2024-0031',
    monitoringPeriod: '01 Oct 2024 – 31 Mar 2025',
    doe: 'Bureau Veritas Carbon Assurance',
    submittedDate: '2025-04-08',
    status: 'In Progress',
    statusColor: 'blue',
    verifiedCredits: '—',
    stage: 3,
    comments: [
      { date: '2025-04-15', from: 'Bureau Veritas', text: 'Monitoring reports received and under desk review.' },
    ],
  },
  {
    id: 'VER-2025-003',
    project: 'Mangrove Restoration Initiative – Borneo',
    projectId: 'P-2024-0042',
    monitoringPeriod: '01 Jan 2025 – 30 Jun 2025',
    doe: 'SGS Carbon Services',
    submittedDate: '2025-07-01',
    status: 'Clarification Requested',
    statusColor: 'yellow',
    verifiedCredits: '—',
    stage: 4,
    comments: [
      { date: '2025-07-22', from: 'SGS Carbon', text: 'Please provide corrected monitoring data for sensor array B-7 (values appear anomalous for March 2025).' },
    ],
  },
]

const stages = ['Reports Submitted', 'Desk Review', 'Site Visit', 'Clarification', 'Draft Report', 'Approved']

const statusClass = (color: string) => {
  const map: Record<string, string> = {
    green: 'bg-green-100 text-green-800',
    blue: 'bg-blue-100 text-blue-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    red: 'bg-red-100 text-red-800',
  }
  return map[color] ?? 'bg-gray-100 text-gray-600'
}
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main class="flex-1 overflow-auto py-8">
      <div class="max-w-5xl mx-auto px-5">

        <!-- Breadcrumb -->
        <nav class="text-sm text-gray-500 mb-4">
          <span class="hover:text-primary cursor-pointer">Dashboard</span>
          <span class="mx-2">/</span>
          <span class="text-gray-800 font-medium">Verification Status</span>
        </nav>

        <!-- Header -->
        <section class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-start justify-between">
              <div>
                <h1 class="text-3xl font-bold text-accent mb-2">Verification Status</h1>
                <p class="text-gray-600">
                  Track DOE verifications of your monitoring reports. A positive verification report is required before requesting carbon credit issuance for each monitoring period.
                </p>
              </div>
              <button class="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary/90 flex items-center">
                <i class="fas fa-chart-line mr-2"></i>Upload Monitoring Reports
              </button>
            </div>
          </div>
        </section>

        <!-- Stats -->
        <div class="grid grid-cols-3 gap-4 mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-accent">3</p>
            <p class="text-sm text-gray-600">Total Verifications</p>
          </div>
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-green-600">1</p>
            <p class="text-sm text-gray-600">Approved</p>
          </div>
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-primary">6,120</p>
            <p class="text-sm text-gray-600">tCO2e Verified</p>
          </div>
        </div>

        <!-- Verification cards -->
        <section class="space-y-6">
          <div
            v-for="ver in verifications"
            :key="ver.id"
            class="bg-white rounded-lg border border-gray-200 p-6"
          >
            <div class="flex items-start justify-between mb-4">
              <div>
                <div class="flex items-center space-x-3 mb-1">
                  <h3 class="font-semibold text-accent">{{ ver.project }}</h3>
                  <span :class="['inline-flex px-2 py-1 rounded-full text-xs font-medium', statusClass(ver.statusColor)]">
                    {{ ver.status }}
                  </span>
                </div>
                <p class="text-sm text-gray-500">
                  Monitoring Period: {{ ver.monitoringPeriod }} &nbsp;·&nbsp; Ref: {{ ver.id }}
                </p>
              </div>
              <div class="text-right text-sm">
                <p class="text-gray-500">Verified Credits</p>
                <p class="font-bold text-primary text-lg">{{ ver.verifiedCredits }} <span v-if="ver.verifiedCredits !== '—'" class="text-xs font-normal text-gray-500">tCO2e</span></p>
              </div>
            </div>

            <!-- Progress steps -->
            <div class="flex items-center mb-4 overflow-x-auto">
              <div
                v-for="(stage, idx) in stages"
                :key="stage"
                class="flex items-center"
              >
                <div class="flex flex-col items-center min-w-[70px]">
                  <div :class="[
                    'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold mb-1',
                    idx + 1 < ver.stage ? 'bg-green-500 text-white' :
                    idx + 1 === ver.stage ? 'bg-primary text-white' :
                    'bg-gray-200 text-gray-500'
                  ]">
                    <i v-if="idx + 1 < ver.stage" class="fas fa-check text-xs"></i>
                    <span v-else>{{ idx + 1 }}</span>
                  </div>
                  <span class="text-xs text-center text-gray-600 leading-tight" style="max-width: 64px;">{{ stage }}</span>
                </div>
                <div v-if="idx < stages.length - 1" :class="[
                  'h-0.5 w-8 mx-1 mb-5',
                  idx + 1 < ver.stage ? 'bg-green-500' : 'bg-gray-200'
                ]"></div>
              </div>
            </div>

            <!-- DOE + comments -->
            <div class="text-sm text-gray-600 mb-3">
              <span class="font-medium text-gray-700">DOE:</span> {{ ver.doe }} &nbsp;·&nbsp;
              <span class="font-medium text-gray-700">Submitted:</span> {{ ver.submittedDate }}
            </div>

            <div class="space-y-2 mb-4">
              <div
                v-for="comment in ver.comments"
                :key="comment.date"
                class="bg-gray-50 rounded-md px-4 py-3 text-sm"
              >
                <p class="text-gray-500 text-xs mb-1">{{ comment.from }} · {{ comment.date }}</p>
                <p class="text-gray-700">{{ comment.text }}</p>
              </div>
            </div>

            <div class="flex items-center space-x-3">
              <button v-if="ver.statusColor === 'green'" class="px-4 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700">
                <i class="fas fa-download mr-1"></i>Download Verification Report
              </button>
              <button v-if="ver.statusColor === 'green'" class="px-4 py-1.5 bg-primary text-white rounded-md text-sm hover:bg-primary/90">
                <i class="fas fa-certificate mr-1"></i>Request Credit Issuance
              </button>
              <button v-if="ver.statusColor === 'yellow'" class="px-4 py-1.5 bg-primary text-white rounded-md text-sm hover:bg-primary/90">
                <i class="fas fa-upload mr-1"></i>Submit Clarification
              </button>
              <button v-if="ver.statusColor === 'blue'" class="px-4 py-1.5 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50">
                <i class="fas fa-eye mr-1"></i>View Reports
              </button>
            </div>
          </div>
        </section>

      </div>
    </main>
  </div>
</template>
